import { DATA_SETS_ENUM_TYPES } from 'constants/dataSets';
import {
  CODE, CRITICALITY_FROM_DOWNSTREAM_WASTE, CRITICALITY_RATING,
  DATE_CLOSED, DATE_OPENED, NAME, PRODUCT_CATEGORY_ID, PRODUCT_TYPE,
  PROFIT_CENTER_LINKS,
  RELIANCE_ON_FREE_NATURAL_RESOURCES, RELIANCE_ON_FREE_SOCIAL_RESOURCES, SOURCING_CONNECTIONS,
  SUPPLIER_LINKS,
  UNIQUE_IDENTIFIER, UPSTREAM_TIER, VALUE_CHAIN_LIST, VALUE_CHAIN_SECTION, VOLUME_QTY, VOLUME_QTY_UNIT,
} from 'constants/formFields';
import { DATA_SETS_DATE_FORMAT, VC_BOARD_TYPES } from 'constants/general';
import dayjs from 'dayjs';
import profitCenterLinksFormatted from 'utils/profitCenterLinksFormatted';
import { sourcingConnectionsFormatted } from 'utils/sourcingConnections';

import createEnumOptions from '../createEnumOptions';
import getFormValue from '../getFormValue';
import {
  getProductTypeOption,
  getProductTypeOptionByValueChain,
  getValueChainByProductType,
} from './productTypeValueChain';

import type { IGetProductDefaultValues } from './interfaces/IGetProductDefaultValues';

function getProductDefaultValues({
  values, enumData, valueChainSectionCurrent, productCategoriesOptions,
}: IGetProductDefaultValues) {
  const {
    name, code, profitCenterLinks, criticalityRating, dateClosed, dateOpened,
    supplierLinks, uniqueIdentifier, volumeQty,
    volumeQtyUnit, circularityFromDownstreamWaste,
    relianceOnFreeNaturalResources, upstreamTier,
    relianceOnFreeSocialResources, valueChainSection,
    productCategoryId, productType, goodsSupplierConnections, manufactureConnections,
  } = values || {};

  const defaultOption = { value: '', label: '' };

  const relianceOnFreeNaturalOptions = createEnumOptions({
    enumData,
    values: relianceOnFreeNaturalResources || [],
    enumKey: DATA_SETS_ENUM_TYPES.relianceOnFreeNaturalResources,
  });

  const relianceOnFreeSocialOptions = createEnumOptions({
    enumData,
    values: relianceOnFreeSocialResources || [],
    enumKey: DATA_SETS_ENUM_TYPES.relianceOnFreeSocialResources,
  });

  const valueChainSectionParts = valueChainSection?.split('. ').map((item) => item.trim()) || [];
  // `productType` is the authoritative source for the board type; fall back to it when the
  // record has no `valueChainSection` so the value chain field can't contradict it (which
  // would otherwise let the product-type sync flip it to the wrong type).
  const valueChainListValue = valueChainSectionParts[0]
    || getValueChainByProductType(productType)
    || VC_BOARD_TYPES.UPSTREAM;
  const valueChainSectionValue = valueChainSectionCurrent || valueChainSectionParts[1] || '';

  const productCategoryOption = productCategoriesOptions?.find((option) => option.value === `${productCategoryId}`);
  const productCategoryValue = productCategoryOption || defaultOption;
  const productTypeValue = getProductTypeOption(productType)
    || getProductTypeOptionByValueChain(valueChainListValue)
    || defaultOption;

  return {
    [PROFIT_CENTER_LINKS]: profitCenterLinksFormatted(profitCenterLinks || []),
    [NAME]: name || '',
    [CODE]: code || '',
    [UNIQUE_IDENTIFIER]: uniqueIdentifier || '',
    [UPSTREAM_TIER]: upstreamTier ? {
      value: upstreamTier, label: upstreamTier,
    } : defaultOption,
    // Names are resolved from the tuples response at render, so only the ids are stored.
    // Still written by the data-sets product form; on the value chain map endpoints the
    // backend derives it from the connections below instead.
    [SUPPLIER_LINKS]: supplierLinks || [],
    // Left undefined when the record reported neither array — a create flow, or a record that
    // has not loaded. The request builder reads that as "say nothing about these edges" rather
    // than as an empty list, which would delete them.
    [SOURCING_CONNECTIONS]: sourcingConnectionsFormatted(goodsSupplierConnections, manufactureConnections),
    [DATE_OPENED]: dateOpened ? dayjs(dateOpened).format(DATA_SETS_DATE_FORMAT) : '',
    [DATE_CLOSED]: dateClosed ? dayjs(dateClosed).format(DATA_SETS_DATE_FORMAT) : '',
    [VOLUME_QTY]: volumeQty || volumeQty === 0 ? String(volumeQty) : '',
    [VOLUME_QTY_UNIT]: volumeQtyUnit ? {
      value: volumeQtyUnit, label: volumeQtyUnit,
    } : defaultOption,
    [RELIANCE_ON_FREE_NATURAL_RESOURCES]: relianceOnFreeNaturalOptions?.length > 0 ? relianceOnFreeNaturalOptions : [],
    [RELIANCE_ON_FREE_SOCIAL_RESOURCES]: relianceOnFreeSocialOptions?.length > 0 ? relianceOnFreeSocialOptions : [],
    [CRITICALITY_RATING]: criticalityRating ? {
      value: criticalityRating, label: criticalityRating,
    } : defaultOption,
    [CRITICALITY_FROM_DOWNSTREAM_WASTE]: getFormValue({ value: circularityFromDownstreamWaste }),
    [VALUE_CHAIN_SECTION]: {
      value: valueChainSectionValue,
      label: valueChainSectionValue,
    },
    [VALUE_CHAIN_LIST]: {
      value: valueChainListValue,
      label: valueChainListValue,
    },
    [PRODUCT_CATEGORY_ID]: productCategoryValue,
    [PRODUCT_TYPE]: productTypeValue,
  };
}

export default getProductDefaultValues;
